import React, { useEffect, useState, useCallback } from "react";
import { Mic } from "lucide-react";
import VoiceEntryDialog from "@/components/VoiceEntryDialog";
import SubscribeDialog from "@/components/SubscribeDialog";
import api from "@/lib/api";

/**
 * Global Floating Action Button for Voice capture.
 * Renders on all authenticated screens via Layout.
 * - Coral/salmon circular FAB (~72px) fixed bottom-right.
 * - Subtle pulse animation during the first 4s to draw attention.
 * - Listens for the `finly:open-voice` window event to open programmatically.
 * - Dispatches `finly:transaction-created` when a transaction is saved.
 */
export default function VoiceFab() {
  const [open, setOpen] = useState(false);
  const [upgradeOpen, setUpgradeOpen] = useState(false);
  const [categories, setCategories] = useState([]);
  const [showHint, setShowHint] = useState(true);

  const loadCategories = useCallback(async () => {
    try {
      const res = await api.get("/categories");
      setCategories(res.data || []);
    } catch {
      // silent: layout-level component, do not toast
    }
  }, []);

  useEffect(() => {
    loadCategories();
  }, [loadCategories]);

  // Allow other components (e.g., WelcomeModal) to open the dialog
  useEffect(() => {
    const handler = () => setOpen(true);
    window.addEventListener("finly:open-voice", handler);
    return () => window.removeEventListener("finly:open-voice", handler);
  }, []);

  // Stop pulse after first 4s
  useEffect(() => {
    const t = setTimeout(() => setShowHint(false), 4000);
    return () => clearTimeout(t);
  }, []);

  const handleCreated = () => {
    window.dispatchEvent(new CustomEvent("finly:transaction-created"));
  };

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        aria-label="Captura por voz"
        data-testid="voice-fab"
        className={`fixed z-50 bottom-24 right-5 md:bottom-8 md:right-8 w-[72px] h-[72px] rounded-full bg-[#FF7F6B] hover:bg-[#FF6B54] active:scale-95 text-white flex items-center justify-center shadow-[0_10px_28px_-6px_rgba(255,107,84,0.6)] transition-all ${
          showHint ? "voice-fab-pulse" : ""
        }`}
      >
        <Mic className="w-8 h-8 stroke-[2.5]" />
        <span className="sr-only">Voz</span>
      </button>

      <VoiceEntryDialog
        open={open}
        onOpenChange={setOpen}
        categories={categories}
        onCreated={handleCreated}
        onLimitReached={() => setUpgradeOpen(true)}
      />
      <SubscribeDialog open={upgradeOpen} onOpenChange={setUpgradeOpen} />
    </>
  );
}
